import {
  ChevronDown,
  ChevronUp,
  VolumeMax,
  VolumeX,
  X,
} from "@untitledui/icons"
import type { AnimationEvent, CSSProperties } from "react"
import { useEffect, useState } from "react"

import { Avatar } from "@/components/ui/avatar"
import type { PlayableKind } from "@/lib/player/playable-item"
import { cn } from "@/lib/utils"
import type { Playlist } from "@/types"

import { bylineCreator, bylineName, chromeLabel, kicker } from "./type-voices"

/** The one sanctioned ring: Byline Blue focus visibility (§3.2). */
export const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-focus-ring)]"

/* ------------------------------------------------------------------ grain */

/**
 * The one sanctioned atmospheric touch: fractal-noise grain at 2.5%,
 * soft-light. The SVG filter must exist in the DOM once per shell — the
 * same inline-defs pattern as the design manual's #canvas-grain.
 */
export function GrainDefs() {
  return (
    <svg
      width="0"
      height="0"
      style={{ position: "absolute" }}
      aria-hidden="true"
    >
      <filter id="jacket-grain">
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.65"
          numOctaves={3}
          stitchTiles="stitch"
        />
      </filter>
    </svg>
  )
}

/**
 * Grain overlay for a paper surface (leaf, sleeve, proof). Parent must be
 * positioned; GrainDefs must be mounted in the same document.
 */
export function GrainLayer({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn("pointer-events-none absolute inset-0", className)}
      style={{
        filter: "url(#jacket-grain)",
        opacity: 0.025,
        mixBlendMode: "soft-light",
      }}
    />
  )
}

/* ---------------------------------------------------------------- duotone */

/**
 * The Ink↔Oat duotone (never grayscale+sepia): luminance via feColorMatrix,
 * then feComponentTransfer gradient-maps shadows → Ink #1F3A66 and
 * highlights → Oat #FBF6ED. Applies to poster imgs only — SVG filters never
 * apply to iframes, so live media always arrives in full color.
 */
export function DuotoneDefs() {
  return (
    <svg
      width="0"
      height="0"
      style={{ position: "absolute" }}
      aria-hidden="true"
    >
      <filter id="jacket-duotone" colorInterpolationFilters="sRGB">
        <feColorMatrix
          type="matrix"
          values="0.2126 0.7152 0.0722 0 0
                  0.2126 0.7152 0.0722 0 0
                  0.2126 0.7152 0.0722 0 0
                  0 0 0 1 0"
        />
        <feComponentTransfer>
          <feFuncR type="table" tableValues="0.1216 0.9843" />
          <feFuncG type="table" tableValues="0.2275 0.9647" />
          <feFuncB type="table" tableValues="0.4 0.9294" />
        </feComponentTransfer>
      </filter>
    </svg>
  )
}

/** The filter reference for anything wearing the duotone (imgs only). */
export const DUOTONE_FILTER = "url(#jacket-duotone)"

/** 45°-rotated dot screen, ~3px pitch, multiplied at 30%. The dots are raw
 * Ink; the oversized inset keeps the rotation from revealing edges. */
export function DotScreen() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -inset-[35%] opacity-30 mix-blend-multiply"
      style={{
        backgroundImage:
          "radial-gradient(circle, rgb(var(--batyr-ink-rgb) / 0.85) 0.6px, transparent 0.75px)",
        backgroundSize: "3px 3px",
        transform: "rotate(45deg)",
      }}
    />
  )
}

/* ----------------------------------------------------------- catalog mark */

const KIND_WORD: Record<PlayableKind, string> = {
  video: "film",
  audio: "audio",
  photo: "still",
  article: "print",
}

/**
 * The kind chip as a catalog mark (§3.6): side letter + per-side track
 * number + kind — authored lowercase (`a3 · film`), rendered as small caps
 * by the label voice. Side A is the first half of the playlist, side B the
 * rest, numbering restarting per side the way a record's label does.
 */
export function CatalogMark({
  index,
  total,
  kind,
  className,
  style,
}: {
  index: number
  total: number
  kind: PlayableKind
  className?: string
  style?: CSSProperties
}) {
  const half = Math.ceil(total / 2)
  const side = index < half ? "a" : "b"
  const number = index < half ? index + 1 : index - half + 1
  return (
    <span
      className={cn("text-batyr-ink/60 whitespace-nowrap", className)}
      style={{ ...chromeLabel, ...style }}
    >
      {side}
      {number} · {KIND_WORD[kind]}
    </span>
  )
}

/* ------------------------------------------------------------------ rules */

/** A hairline rule placed the way a layout artist rules a page. */
export function HairlineRule({ className }: { className?: string }) {
  return <div aria-hidden="true" className={cn("jacket-rule", className)} />
}

/* ----------------------------------------------------------------- byline */

/**
 * The Byline — attribution as a first-class element on every jacket face
 * (§3.6): 24px curator avatar + curator name at 13.5/600 in Ink, then the
 * media creator in slate after an em-dash (mockup pass — Byline Blue
 * retired for attribution). Curator identity comes from the playlist
 * owner (PlayableItem.actor is nil for track-backed items).
 */
export function BylineBlock({
  playlist,
  mediaCreator,
  className,
}: {
  playlist: Playlist
  mediaCreator?: string | null
  className?: string
}) {
  const curator = playlist.user
  if (!curator && !mediaCreator) return null
  // `avatar_url` is the wire field (PlaylistOwnerResource) but the User type
  // only names `avatar` — reach through the index signature and narrow.
  const avatarUrl =
    curator && typeof curator.avatar_url === "string"
      ? curator.avatar_url
      : undefined
  return (
    <div className={cn("flex min-w-0 items-center gap-2", className)}>
      {curator && (
        <Avatar size="xs" name={curator.name} avatarUrl={avatarUrl} />
      )}
      <p className="min-w-0 truncate">
        {curator && <span style={bylineName}>{curator.name}</span>}
        {curator && mediaCreator && <span style={bylineCreator}>{" — "}</span>}
        {mediaCreator && <span style={bylineCreator}>{mediaCreator}</span>}
      </p>
    </div>
  )
}

/* ------------------------------------------------------------- top chrome */

/**
 * Shared mute/close chrome — square paper buttons (no black circles, no
 * blur, radius ≤ 4px). `muteSuppressed` swaps the toggle for a small-caps
 * hint while an unbridged audio embed is active: toggling would rebuild the
 * iframe src and reload the embed with a visible flash.
 */
export function TopChrome({
  isMuted,
  muteSuppressed = false,
  onToggleMute,
  onClose,
}: {
  isMuted: boolean
  muteSuppressed?: boolean
  onToggleMute: () => void
  onClose: () => void
}) {
  return (
    <div className="pointer-events-none absolute top-3 right-3 z-30 flex items-center gap-2">
      {muteSuppressed ? (
        <span
          className="bg-surface-elevated text-batyr-ink/60 rounded-xs px-2 py-1 shadow-xs"
          style={chromeLabel}
        >
          sound lives in the player
        </span>
      ) : (
        <button
          type="button"
          onClick={onToggleMute}
          className={cn(
            "text-batyr-ink bg-surface-elevated pointer-events-auto inline-flex size-9 cursor-pointer items-center justify-center rounded-xs shadow-xs",
            FOCUS_RING,
          )}
          aria-label={isMuted ? "Unmute" : "Mute"}
        >
          {isMuted ? (
            <VolumeX className="size-4" />
          ) : (
            <VolumeMax className="size-4" />
          )}
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        className={cn(
          "text-batyr-ink bg-surface-elevated pointer-events-auto inline-flex size-9 cursor-pointer items-center justify-center rounded-xs shadow-xs",
          FOCUS_RING,
        )}
        aria-label="Close player"
      >
        <X className="size-4" />
      </button>
    </div>
  )
}

/* ----------------------------------------------------------- playlist mark */

/**
 * The playlist's identity — title + curator — on the top chrome row,
 * opposite the mute/close squares. This replaces the card's vertical
 * spine (mockup pass, verdict two: the rotated strip was inherent noise
 * pinching the kicker/title): the same words, off the jacket, so the
 * face keeps its whitespace. Hidden while open — the parked dock spine
 * owns the left edge then.
 */
export function PlaylistMark({
  title,
  curator,
  hidden,
}: {
  title: string
  curator?: string | null
  hidden: boolean
}) {
  if (hidden) return null
  return (
    <div
      data-component="playlist-mark"
      className="pointer-events-none absolute top-3 left-3 z-30 flex h-9 max-w-[45vw] items-center"
    >
      <span className="truncate" style={kicker}>
        {title}
        {curator ? ` — ${curator}` : ""}
      </span>
    </div>
  )
}

/* ---------------------------------------------------------- end-of-stack */

/**
 * The colophon end-cap: fills the peek void under the final slide so the
 * stack visibly ends (there is no "next jacket" to peek). It doubles as the
 * pointer sibling of the swipe/wheel at the end — a tap turns past the stack
 * into the outro (onActivate). tabIndex={-1}: ArrowDown already owns that path.
 */
export function EndOfStackCap({
  visible,
  onActivate,
}: {
  visible: boolean
  onActivate?: () => void
}) {
  if (!visible) return null
  return (
    <button
      type="button"
      onClick={onActivate}
      tabIndex={-1}
      aria-label="End of the stack"
      className={cn(
        "absolute inset-x-0 bottom-0 z-10 flex h-[14px] cursor-pointer items-center gap-2 px-6",
        FOCUS_RING,
      )}
    >
      <div className="jacket-rule flex-1" />
      <span
        className="text-batyr-ink/50"
        style={{ ...chromeLabel, fontSize: 10 }}
      >
        end of the stack
      </span>
      <div className="jacket-rule flex-1" />
    </button>
  )
}

/* -------------------------------------------------------------- peek hint */

/** Idle stretch before the caret bumps its amplitude once as a "still here". */
const PEEK_IDLE_MS = 6500

/**
 * The one honest scroll affordance: a low-opacity caret sitting in the peek
 * void the centred carousel leaves at the top/bottom of the viewport. It marks
 * that the stack turns up/down and doubles as the pointer sibling of the swipe
 * (tap to turn one plate) — no faux sleeve edge pretending to be paper.
 * tabIndex={-1} keeps it out of the tab order, since arrows and j/k already
 * carry the keyboard path.
 *
 * Motion (feed.css): the caret breathes — a soft looping nudge toward its own
 * edge with an opacity swell and a ~1.7s hold between cycles, a ghost caret
 * trailing behind for depth. `dimmed` fades the whole thing out the moment the
 * stack scrolls (its job is done); after a few seconds of stillness the shell's
 * idle timer here arms one bigger nudge as a "still here" hint. Reduced motion
 * drops the travel and holds a static caret — the CSS owns that fork.
 */
export function PeekHint({
  edge,
  label,
  onActivate,
  dimmed = false,
  reduce = false,
}: {
  edge: "top" | "bottom"
  label: string
  onActivate: () => void
  /** The stack is scrolling — fade out; the caret has said its piece. */
  dimmed?: boolean
  reduce?: boolean
}) {
  const Chevron = edge === "top" ? ChevronUp : ChevronDown
  const [boosting, setBoosting] = useState(false)

  // Arm the "still here" bump after a stretch of stillness. Re-arms whenever
  // the caret settles from a scroll (dimmed→false), the neighbour changes
  // (label), or the prior boost finishes (boosting→false). Never while dimmed,
  // reduced, or already mid-boost — those own the clock.
  useEffect(() => {
    if (dimmed || reduce || boosting) return
    const id = window.setTimeout(() => setBoosting(true), PEEK_IDLE_MS)
    return () => window.clearTimeout(id)
  }, [dimmed, reduce, boosting, label])

  const onStackAnimationEnd = (event: AnimationEvent<HTMLSpanElement>) => {
    // Only the one-shot boost ends (the nudge/ghost loops are infinite).
    if (event.animationName === "jacket-peek-boost") setBoosting(false)
  }

  return (
    <button
      type="button"
      onClick={onActivate}
      tabIndex={-1}
      aria-label={`${edge === "top" ? "Previous" : "Next"} — ${label}`}
      style={{ "--peek-dir": edge === "top" ? -1 : 1 } as CSSProperties}
      className={cn(
        // h-10 == PEEK_PX (40px), the void the centred carousel leaves. The
        // caret centres in that void, then translates half a void toward the
        // jacket so it rests roughly midway between the void and the jacket —
        // the screen edges belong to other chrome. On narrow viewports the
        // crate rail hides, so the caret grows and pulls further inward to
        // read as the clear navigational prompt.
        "jacket-peek absolute inset-x-0 z-10 flex h-10 cursor-pointer items-center justify-center transition-opacity duration-300",
        edge === "top"
          ? "top-0 translate-y-[6.75rem] sm:translate-y-5"
          : "bottom-0 -translate-y-[6.75rem] sm:-translate-y-5",
        dimmed ? "pointer-events-none opacity-0" : "opacity-100",
        FOCUS_RING,
      )}
    >
      <span
        onAnimationEnd={onStackAnimationEnd}
        className={cn(
          "jacket-peek-stack relative inline-flex items-center justify-center",
          boosting && "is-boosting",
        )}
      >
        <Chevron
          aria-hidden="true"
          className="jacket-peek-nudge text-batyr-ink size-9 sm:size-6"
          strokeWidth={2}
        />
        {/* The trailing ghost: fades in ~120ms behind on the down-travel to
            imply the depth of the plate the caret points toward. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <Chevron
            className="jacket-peek-ghost text-batyr-ink size-9 sm:size-6"
            strokeWidth={2}
          />
        </span>
      </span>
    </button>
  )
}
